import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/me`).then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  // Silent session renewal: on a 401 try the refresh cookie once and replay the request; otherwise sign out.
  useEffect(() => {
    let refreshing = null;
    const id = axios.interceptors.response.use(undefined, async (error) => {
      const cfg = error.config || {};
      const url = cfg.url || "";
      if (error.response?.status !== 401 || cfg._retried || url.includes("/auth/")) throw error;
      try {
        refreshing = refreshing || axios.post(`${API}/auth/refresh`).finally(() => { refreshing = null; });
        const { data } = await refreshing;
        setUser(data);
        return axios({ ...cfg, _retried: true });
      } catch (e) {
        setUser(false);
        throw error;
      }
    });
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
  };

  // Self-registration lands in the admin approval queue — do NOT log the user in.
  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { console.debug("Logout request failed (clearing session anyway):", e?.message); }
    setUser(false);
  };

  const forgotPassword = async (email) => (await axios.post(`${API}/auth/forgot-password`, { email })).data;
  const resetPassword = async (token, new_password) => (await axios.post(`${API}/auth/reset-password`, { token, new_password })).data;
  const changePassword = async (current_password, new_password) => (await axios.post(`${API}/auth/change-password`, { current_password, new_password })).data;

  return <AuthContext.Provider value={{ user, login, register, logout, forgotPassword, resetPassword, changePassword }}>{children}</AuthContext.Provider>;
}

// Admins see everything; report consumers see only the pages an admin has granted them.
export function isAdmin(user) {
  return user?.role === "admin";
}
export function canAccess(user, pageKey) {
  if (!user) return false;
  if (user.role === "admin") return true;
  return (user.allowed_pages || []).includes(pageKey);
}

export const useAuth = () => useContext(AuthContext);
export { API };
